/**
 * Shared client-side sign-out.
 *
 * IMPORTANT: logging out MUST end with a TOP-LEVEL browser navigation to the
 * OIDC end-session URL — NOT a `fetch()` and then `window.location = "/"`.
 * The auth-domain OIDC `_session` cookie is `SameSite=Lax`, so it is only sent
 * on a top-level navigation, never on a cross-origin fetch subrequest. If the
 * caller just fetches `/api/auth/logout` and then goes home, the OIDC session
 * is never cleared and the next sign-in silently re-authenticates with no
 * credential prompt.
 *
 * `POST /api/auth/logout` clears the publicWeb session cookie and returns the
 * OIDC logout URL as JSON; we then navigate the browser to it. That URL bounces
 * through the auth end-session (which clears the OIDC session) and back to
 * `/login`. Every header / user-menu sign-out button should call this — do not
 * re-implement the fetch inline (that duplication is what let this bug spread
 * across themes).
 */
export async function signOutAndRedirect(): Promise<void> {
  try {
    const response = await fetch("/api/auth/logout", {
      method: "POST",
      credentials: "include",
    });
    const data = await response.json().catch(() => ({} as { logoutUrl?: string }));
    // Top-level navigation to the OIDC end-session (falls back to /login if the
    // URL is somehow absent — logout still leaves the app).
    window.location.href = data?.logoutUrl || "/login";
    return;
  } catch {
    // Network error — still leave the authenticated app.
    window.location.href = "/login";
  }
}
